/**
 * Shared copy for family invitations — used by the Family screen's share flow
 * (composer prefill) and the demo chat. `role` is the SENDER's role: a parent
 * offers support, a learner asks a parent to join. The sender's profile name
 * is always included so the recipient knows exactly who is asking.
 */
export function buildFamilyInviteMessage({
  role,
  name,
  link,
}: {
  role: 'parent' | 'child'
  name: string
  link: string
}): string {
  return role === 'parent'
    ? `Hi, I'm ${name} — I'd love to support you on Taderok 💛 ${link}`
    : `Hi, I'm ${name} — I'd love you to be my parent in Taderok 💛 ${link}`
}

/**
 * Reasons a receiver can pick when declining an invitation. Framed as
 * "not spam / sent by mistake" cases: honest, non-blaming options the sender
 * can act on (regenerate, don't ask again), plus space for a personal note.
 */
export const FAMILY_DECLINE_CAUSES = [
  'It was sent by mistake',
  "I don't know this person",
  "I'm not ready to connect on Taderok yet",
  "I don't want to receive family invitations",
] as const

/**
 * The polite first-person reply sent back to the invite sender when someone
 * declines — warm, specific, and it includes the decliner's name so the
 * sender knows exactly who answered.
 */
export function buildFamilyDeclineMessage({
  name,
  cause,
  note,
}: {
  name: string
  cause: string
  note?: string
}): string {
  const reason = note?.trim() ? `${cause} — ${note.trim()}` : cause
  return `I'm ${name} — I'm so sorry, I can't accept your invitation right now. (${reason}) 💛`
}